import { DataProvider, fetchUtils } from 'react-admin';

const API_URL = process.env.REACT_APP_API_URL || 'https://api-qa.nexuska.com/api';
const CSRF_COOKIE_NAME = 'admin_csrf';

const listResourceMap: Record<string, string> = {
  users: 'users',
  workshops: 'workshops/admin/all',
  bookings: 'bookings',
  services: 'services/admin/all',
  cars: 'cars',
  serviceTemplates: 'services/templates/all',
};

const defaultResourceMap: Record<string, string> = {
  users: 'users',
  workshops: 'workshops',
  bookings: 'bookings',
  services: 'services',
  cars: 'cars',
  serviceTemplates: 'services/templates',
};

const getApiResource = (resource: string, operation: 'list' | 'default'): string => {
  if (operation === 'list') {
    return listResourceMap[resource] || resource;
  }
  return defaultResourceMap[resource] || resource;
};

function getCookie(name: string): string | null {
  const target = `${name}=`;
  const found = document.cookie
    .split(';')
    .map((part) => part.trim())
    .find((part) => part.startsWith(target));

  if (!found) {
    return null;
  }

  return decodeURIComponent(found.substring(target.length));
}

async function ensureCsrfToken(): Promise<string | null> {
  const existing = getCookie(CSRF_COOKIE_NAME);
  if (existing) {
    return existing;
  }

  const response = await fetch(`${API_URL}/admin/auth/csrf`, {
    method: 'GET',
    credentials: 'include',
  });

  if (!response.ok) {
    return null;
  }

  const payload = await response.json();
  if (payload?.csrfToken && typeof payload.csrfToken === 'string') {
    return payload.csrfToken;
  }

  return getCookie(CSRF_COOKIE_NAME);
}

const needsCsrfToken = (method: string): boolean => {
  return ['POST', 'PUT', 'PATCH', 'DELETE'].includes(method.toUpperCase());
};

const httpClient = async (url: string, options: any = {}) => {
  const method = (options.method || 'GET').toUpperCase();

  if (!options.headers) {
    options.headers = new Headers({
      Accept: 'application/json',
      'Content-Type': 'application/json',
    });
  }

  options.credentials = 'include';

  if (needsCsrfToken(method)) {
    const csrfToken = await ensureCsrfToken();
    if (csrfToken) {
      options.headers.set('X-CSRF-Token', csrfToken);
    }
  }

  return fetchUtils.fetchJson(url, options);
};

const mapServiceTemplateRecord = (record: any) => ({
  ...record,
  id: record?.id ?? record?.name,
});

export const dataProvider: DataProvider = {
  getList: async (resource, params) => {
    const { page, perPage } = params.pagination || { page: 1, perPage: 10 };
    const { field, order } = params.sort || { field: 'id', order: 'ASC' };
    const apiResource = getApiResource(resource, 'list');
    let url = `${API_URL}/${apiResource}?page=${page}&limit=${perPage}`;

    if (field) {
      url += `&sort=${field}&order=${order}`;
    }

    Object.keys(params.filter || {}).forEach((key) => {
      url += `&${key}=${params.filter[key]}`;
    });

    const { json } = await httpClient(url);

    if (Array.isArray(json)) {
      const data = resource === 'serviceTemplates' ? json.map(mapServiceTemplateRecord) : json;
      return {
        data,
        total: data.length,
      };
    }

    let data: any[] = [];
    let total = 0;

    if (resource === 'users' && json.users) {
      data = json.users;
      total = json.total || 0;
    } else if (resource === 'workshops' && json.workshops) {
      data = json.workshops;
      total = json.total || 0;
    } else if (resource === 'bookings' && json.bookings) {
      data = json.bookings;
      total = json.total || 0;
    } else if (resource === 'services' && json.services) {
      data = json.services;
      total = json.total || 0;
    } else if (resource === 'cars' && json.cars) {
      data = json.cars;
      total = json.total || 0;
    } else {
      data = json.data || json.items || [];
      total = json.total || json.count || json.length || 0;
    }

    return { data, total };
  },

  getOne: async (resource, params) => {
    if (resource === 'serviceTemplates') {
      const { json } = await httpClient(`${API_URL}/services/templates/all`);
      const templates = Array.isArray(json) ? json : [];
      const template = templates.find((item: any) => item.name === params.id);
      if (!template) {
        throw new Error(`Service template not found: ${params.id}`);
      }
      return { data: mapServiceTemplateRecord(template) };
    }

    const apiResource = getApiResource(resource, 'default');
    const { json } = await httpClient(`${API_URL}/${apiResource}/${params.id}`);
    return { data: json.data || json };
  },

  getMany: async (resource, params) => {
    const data = await Promise.all(
      params.ids.map(async (id) => {
        const result = await dataProvider.getOne(resource, { id, meta: params.meta });
        return result.data;
      })
    );

    return { data };
  },

  getManyReference: async (resource, params) => {
    const { page, perPage } = params.pagination || { page: 1, perPage: 10 };
    const { field, order } = params.sort || { field: 'id', order: 'ASC' };
    const apiResource = getApiResource(resource, 'list');

    let url = `${API_URL}/${apiResource}?page=${page}&limit=${perPage}`;
    url += `&${params.target}=${params.id}`;

    if (field) {
      url += `&sort=${field}&order=${order}`;
    }

    const { json } = await httpClient(url);
    return {
      data: json.data || json,
      total: json.total || json.length || 0,
    };
  },

  create: async (resource, params) => {
    const apiResource = getApiResource(resource, 'default');
    const payload =
      resource === 'serviceTemplates'
        ? {
            name: params.data?.name,
            description: params.data?.description,
            category: params.data?.category,
          }
        : params.data;
    const { json } = await httpClient(`${API_URL}/${apiResource}`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    const data = json.data || json;
    return {
      data: resource === 'serviceTemplates' ? mapServiceTemplateRecord(data) : data,
    };
  },

  update: async (resource, params) => {
    const apiResource = getApiResource(resource, 'default');
    const payload =
      resource === 'serviceTemplates'
        ? {
            description: params.data?.description,
            category: params.data?.category,
          }
        : params.data;
    const { json } = await httpClient(`${API_URL}/${apiResource}/${params.id}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    });
    const data = json.data || json;
    return {
      data: resource === 'serviceTemplates' ? mapServiceTemplateRecord(data) : data,
    };
  },

  updateMany: async (resource, params) => {
    const apiResource = getApiResource(resource, 'default');
    const query = {
      filter: JSON.stringify({ id: params.ids }),
    };
    await httpClient(`${API_URL}/${apiResource}?${new URLSearchParams(query)}`, {
      method: 'PATCH',
      body: JSON.stringify(params.data),
    });
    return { data: params.ids };
  },

  delete: async (resource, params) => {
    const apiResource = getApiResource(resource, 'default');
    await httpClient(`${API_URL}/${apiResource}/${params.id}`, {
      method: 'DELETE',
    });
    if (resource === 'serviceTemplates') {
      return {
        data: mapServiceTemplateRecord(params.previousData),
      };
    }
    return { data: params.previousData as any };
  },

  deleteMany: async (resource, params) => {
    const apiResource = getApiResource(resource, 'default');
    const query = {
      filter: JSON.stringify({ id: params.ids }),
    };
    await httpClient(`${API_URL}/${apiResource}?${new URLSearchParams(query)}`, {
      method: 'DELETE',
    });
    return { data: params.ids };
  },
};
